import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  updateDoc,
  where
} from 'firebase/firestore';
import { DoctorRepository, FindDoctorsOptions } from '../../../domain/contracts/repositories';
import { Doctor } from '../../../domain/entities';
import { SpecialtyService } from '../../../domain/services';
import db from '../../../setup/firebase/firebase';
import { FirebaseDoctor } from '../entities';
import { DoctorMapper } from '../mappers';

export class FirebaseDoctorRepository implements DoctorRepository {
  constructor(private readonly specialtyService: SpecialtyService) {}

  readonly COLLECTION_PATH = 'doctors';

  async findAll(): Promise<Doctor[]> {
    const q = query(collection(db, this.COLLECTION_PATH));
    const querySnapshot = await getDocs(q);
    const doctors = await Promise.all(
      querySnapshot.docs.map(async (doc) => {
        const data = doc.data() as FirebaseDoctor;
        data.id = doc.id;
        const specialty = await this.specialtyService.getOne(data.specialtyCode);
        return DoctorMapper.toDomain(data, specialty);
      })
    );
    return doctors;
  }

  async find({ specialtyCode, medicalCenter, currentDate }: FindDoctorsOptions): Promise<Doctor[]> {
    const q = query(
      collection(db, this.COLLECTION_PATH),
      where('specialtyCode', '==', specialtyCode),
      where('medicalCenter', '==', medicalCenter)
    );
    const querySnapshot = await getDocs(q);
    let doctors = await Promise.all(
      querySnapshot.docs.map(async (doc) => {
        const data = doc.data() as FirebaseDoctor;
        data.id = doc.id;
        const specialty = await this.specialtyService.getOne(data.specialtyCode);
        return DoctorMapper.toDomain(data, specialty);
      })
    );
    doctors = doctors.filter((doctor) => {
      const availableDaysInString = doctor.availableDays.map((date) => date.toDateString());
      return availableDaysInString.includes(currentDate.toDateString());
    });
    return doctors;
  }

  async findOne(id: string): Promise<Doctor> {
    const docRef = doc(db, this.COLLECTION_PATH, id);
    const docSnap = await getDoc(docRef);
    const data = docSnap.data() as FirebaseDoctor;
    const specialty = await this.specialtyService.getOne(data.specialtyCode);
    return DoctorMapper.toDomain(data, specialty);
  }

  async updateAvailableDays(doctorId: string, availableDays: number[]): Promise<void> {
    const doctorDocRef = doc(db, this.COLLECTION_PATH, doctorId);
    await updateDoc(doctorDocRef, {
      availableDays
    });
  }

  async create(doctor: Doctor): Promise<void> {
    const firebaseDoctor = DoctorMapper.toFirebaseEntity(doctor);
    await setDoc(doc(db, this.COLLECTION_PATH, doctor.id), firebaseDoctor);
  }
}
